// Independent, client-side product search.
//
// Shopify's built-in search/predictive-search index can drift out of sync with
// the real catalog (e.g. after a bulk CSV re-import), silently returning zero
// or partial results for products that genuinely exist and are published. This
// searches a JSON snapshot of the live catalog (rendered into the page by
// snippets/product-search-data.liquid) directly in the browser instead, so
// results never depend on that index being fresh.
(function () {
  var moneyFormat = (window.theme && window.theme.moneyFormat) || '${{amount}}';

  function normalize(value) {
    return (value || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');
  }

  function formatMoney(cents) {
    var amount = (Number(cents) || 0) / 100;
    var parts = amount.toFixed(2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    var formatted = parts[0] + ',' + parts[1];
    return moneyFormat.replace('{{amount}}', formatted).replace('{{ amount }}', formatted);
  }

  function escapeHtml(value) {
    var div = document.createElement('div');
    div.textContent = value || '';
    return div.innerHTML;
  }

  var productsCache = [];
  var pendingCallbacks = [];
  var loaded = false;

  function annotate(product) {
    product.__haystack = normalize([product.title, product.vendor, product.type, product.tags].join(' '));
    product.__titleNorm = normalize(product.title);
    return product;
  }

  function parseChunk(root) {
    var script = root && root.querySelector('#ProductSearchDataChunk');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent).map(annotate);
    } catch (e) {
      return [];
    }
  }

  function onLoaded() {
    loaded = true;
    pendingCallbacks.forEach(function (cb) {
      cb(productsCache);
    });
    pendingCallbacks = [];
  }

  function loadAllProducts() {
    var cacheKey = 'independentSearchProducts_v2';
    var cached;
    try {
      cached = sessionStorage.getItem(cacheKey);
    } catch (e) {
      cached = null;
    }
    if (cached) {
      try {
        var cachedProducts = JSON.parse(cached).map(annotate);
        // A near-empty cached result almost certainly means a previous load
        // hit a transient error (e.g. a Liquid error from a product with
        // missing data breaking the JSON mid-way). Don't trust it — refetch.
        if (cachedProducts.length > 20) {
          productsCache = cachedProducts;
          onLoaded();
          return;
        }
      } catch (e) {
        // fall through to a fresh fetch
      }
    }

    var mount = document.getElementById('ProductSearchDataMount');
    productsCache = parseChunk(mount);
    var pagesEl = mount && mount.querySelector('#ProductSearchDataPages');
    var totalPages = pagesEl ? parseInt(pagesEl.dataset.pages, 10) || 1 : 1;

    var fetches = [];
    for (var page = 2; page <= totalPages; page++) {
      fetches.push(
        fetch(location.pathname + '?section_id=product-search-data&page=' + page)
          .then(function (res) {
            return res.text();
          })
          .then(function (text) {
            var doc = new DOMParser().parseFromString(text, 'text/html');
            productsCache = productsCache.concat(parseChunk(doc));
          })
          .catch(function () {})
      );
    }

    Promise.all(fetches).then(function () {
      if (productsCache.length > 20) {
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(productsCache));
        } catch (e) {}
      }
      onLoaded();
    });
  }

  function whenProductsReady(callback) {
    if (loaded) {
      callback(productsCache);
    } else {
      pendingCallbacks.push(callback);
    }
  }

  loadAllProducts();

  function searchProducts(query, limit) {
    limit = limit || 10;
    var q = normalize(query).trim();
    if (!q) return [];
    var words = q.split(/\s+/).filter(Boolean);
    var products = productsCache;

    var matches = products.filter(function (product) {
      return words.every(function (word) {
        return product.__haystack.indexOf(word) !== -1;
      });
    });

    matches.sort(function (a, b) {
      var aStarts = a.__titleNorm.indexOf(q) === 0 ? 0 : 1;
      var bStarts = b.__titleNorm.indexOf(q) === 0 ? 0 : 1;
      if (aStarts !== bStarts) return aStarts - bStarts;

      var aIdx = a.__titleNorm.indexOf(words[0]);
      var bIdx = b.__titleNorm.indexOf(words[0]);
      aIdx = aIdx === -1 ? 999 : aIdx;
      bIdx = bIdx === -1 ? 999 : bIdx;
      if (aIdx !== bIdx) return aIdx - bIdx;

      return a.title.length - b.title.length;
    });

    return matches.slice(0, limit);
  }

  window.IndependentSearch = {
    searchProducts: searchProducts,
    formatMoney: formatMoney,
    whenProductsReady: whenProductsReady,
  };

  // ---- Predictive search dropdown: swap the data source, keep the UI ----

  function buildPredictiveMarkup(term, results) {
    var termEscaped = escapeHtml(term);
    var listItems = results
      .map(function (product, index) {
        var image = product.image
          ? '<img class="predictive-search__image" src="' +
            escapeHtml(product.image) +
            '" alt="" width="50" height="50" loading="lazy">'
          : '';
        return (
          '<li id="predictive-search-option-product-' +
          (index + 1) +
          '" class="predictive-search__list-item" role="option" aria-selected="false">' +
          '<a href="' +
          escapeHtml(product.url) +
          '" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">' +
          image +
          '<div class="predictive-search__item-content predictive-search__item-content--centered">' +
          '<p class="predictive-search__item-heading h5">' +
          escapeHtml(product.title) +
          '</p>' +
          '</div></a></li>'
        );
      })
      .join('');

    var productsBlock = '';
    if (results.length > 0) {
      productsBlock =
        '<div class="predictive-search__result-group">' +
        '<div><h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">Productos</h2>' +
        '<ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products">' +
        listItems +
        '</ul></div></div>';
    }

    var wrapperOpen =
      '<div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper' +
      (results.length === 0 ? ' predictive-search__results-groups-wrapper--no-products' : '') +
      ' predictive-search__results-groups-wrapper--no-suggestions">';

    var searchForButton =
      '<div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">' +
      '<button class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" tabindex="-1" role="option" aria-selected="false">' +
      '<span data-predictive-search-search-for-text>Buscar &ldquo;' +
      termEscaped +
      '&rdquo;</span>' +
      '<span class="svg-wrapper">&rarr;</span>' +
      '</button></div>';

    var liveRegion =
      '<span class="hidden" data-predictive-search-live-region-count-value data-total-results="' +
      results.length +
      '">' +
      (results.length === 0
        ? 'No se encontraron resultados para “' + termEscaped + '”'
        : results.length + ' resultados para “' + termEscaped + '”') +
      '</span>';

    return wrapperOpen + productsBlock + '</div>' + searchForButton + liveRegion;
  }

  function patchPredictiveSearch() {
    if (typeof PredictiveSearch === 'undefined' || !PredictiveSearch.prototype) return false;

    PredictiveSearch.prototype.getSearchResults = function (searchTerm) {
      this.setLiveRegionLoadingState();

      var results = searchProducts(searchTerm, 10);
      var markup = buildPredictiveMarkup(searchTerm, results);

      var searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      this.renderSearchResults(markup);
      searchDeferred?.resolve({ totalCount: results.length });
    };

    return true;
  }

  if (!patchPredictiveSearch()) {
    document.addEventListener('DOMContentLoaded', function () {
      if (!patchPredictiveSearch()) {
        // predictive-search.js may not be present on this page/theme state; retry once more shortly.
        setTimeout(patchPredictiveSearch, 300);
      }
    });
  }

  // ---- Full /search results page: render the grid from local data too ----

  function ensureCardProductStyles() {
    if (document.querySelector('link[href*="component-card-product.css"]')) return;
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = window.IndependentSearchAssetUrls.cardProductCss;
    document.head.appendChild(link);
  }

  function buildResultCard(product) {
    var image = product.image
      ? '<img class="card-product__image--primary" src="' +
        escapeHtml(product.image) +
        '" alt="" loading="lazy" width="600" height="750">'
      : '';

    var badgeText = '';
    if (!product.available) {
      badgeText = 'Agotado';
    } else if (product.compare_at_price && product.compare_at_price > product.price) {
      var pct = Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100);
      badgeText = 'Oferta −' + pct + '%';
    }
    var badge = badgeText
      ? '<span class="card-product__badge' +
        (!product.available ? ' card-product__badge--sold-out' : '') +
        '">' +
        badgeText +
        '</span>'
      : '';

    var variantLine =
      !product.has_only_default_variant && product.variant_title
        ? '<span class="card-product__active-variant">· ' + escapeHtml(product.variant_title) + '</span>'
        : '';

    var comparePrice =
      product.compare_at_price && product.compare_at_price > product.price
        ? '<span class="card-product__price-compare">' + formatMoney(product.compare_at_price) + '</span>'
        : '';

    return (
      '<li class="grid__item">' +
      '<div class="card-product">' +
      '<div class="card-product__media-wrapper" style="--card-product-ratio: 4 / 5;">' +
      '<a href="' +
      escapeHtml(product.url) +
      '" class="card-product__media-link" aria-label="' +
      escapeHtml(product.title) +
      '">' +
      image +
      '</a>' +
      badge +
      '</div>' +
      '<div class="card-product__content">' +
      '<span class="card-product__vendor">' +
      escapeHtml(product.vendor) +
      '</span>' +
      '<h3 class="card-product__title"><a href="' +
      escapeHtml(product.url) +
      '">' +
      escapeHtml(product.title) +
      '</a></h3>' +
      variantLine +
      '<div class="card-product__price-group">' +
      '<span class="card-product__price">' +
      formatMoney(product.price) +
      '</span>' +
      comparePrice +
      '</div>' +
      '</div></div></li>'
    );
  }

  function renderSearchPage() {
    var grid = document.getElementById('product-grid');
    if (!grid) return;

    ensureCardProductStyles();

    var params = new URLSearchParams(location.search);
    var term = params.get('q');
    if (!term) return;

    var results = searchProducts(term, 60);

    var countEl = document.querySelector('[role="status"] p, .template-search__header p[role="status"]');
    var headerStatus = document.querySelector('.template-search__header p[role="status"]');
    if (headerStatus) {
      headerStatus.textContent =
        results.length > 0
          ? results.length + ' resultados para "' + term + '"'
          : 'No se encontraron resultados para "' + term + '"';
    }

    if (results.length === 0) {
      grid.className = 'template-search__results collection collection--empty page-width';
      grid.innerHTML =
        '<div class="title-wrapper center"><h2 class="title title--primary">No se encontraron resultados para &ldquo;' +
        escapeHtml(term) +
        '&rdquo;</h2></div>';
      return;
    }

    grid.className = 'template-search__results collection page-width';
    grid.innerHTML =
      '<ul class="grid product-grid grid--2-col-tablet-down grid--4-col-desktop independent-search-grid" role="list">' +
      results.map(buildResultCard).join('') +
      '</ul>';
  }

  function initSearchPage() {
    renderSearchPage();
    whenProductsReady(renderSearchPage);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSearchPage);
  } else {
    initSearchPage();
  }
})();
